import { useEffect, useState } from 'react'
import { urlForImage } from 'lib/sanity.image'
import { getUrlFromLink, handleize } from 'lib/helpers'
import { AnimatePresence, motion } from 'motion/react'
import { PortableText } from '@portabletext/react'

import Link from 'next/link'
import Image from 'next/image'
import { PlusIcon } from 'components/Global/PlusIcon'
import { CloseIcon } from 'components/Global/CloseIcon'
import { Button } from 'components/Global/Button'

export const InfoGrid = ({ items }) => {
  const [activeTab, setActiveTab] = useState<null | string>(null)

  useEffect(() => {
    const tab = document.querySelector(`#${activeTab}`)
    tab?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    })
  }, [activeTab])

  return (
    <div>
      <AnimatePresence>
        {activeTab !== null ? (
          <InfoOverlay
            items={items}
            setActiveTab={setActiveTab}
            activeTab={activeTab}
          />
        ) : null}
      </AnimatePresence>
      <div className="grid grid-cols-1 gap-10 p-20 md:grid-cols-3 md:gap-20">
        {items.map((item) => (
          <InfoItem key={item._key} setActiveTab={setActiveTab} {...item} />
        ))}
      </div>
    </div>
  )
}

const InfoItem = ({ title, image, setActiveTab }) => {
  return (
    <div className="leading-0 relative aspect-[1/1.2] overflow-hidden rounded-8">
      <Image
        className="scale-105"
        src={urlForImage(image).width(500).dpr(3).url()}
        fill
        style={{ objectFit: 'cover' }}
        alt={image.alt ?? `Display Image for CHNGE ${title}`}
        placeholder="blur"
        blurDataURL={image.lqip}
      />
      <div className="absolute bottom-14 flex w-full justify-between px-14 text-12 leading-120 text-darkGray">
        <div className="rounded-6 bg-[#EAEAEA] px-12 py-8">{title}</div>
        <button
          onClick={() => setActiveTab(handleize(title))}
          className="rounded-6 bg-[#EAEAEA] px-10 py-8"
        >
          <PlusIcon className="h-12 w-12" />
        </button>
      </div>
    </div>
  )
}

const InfoOverlay = ({ items, setActiveTab, activeTab }) => {
  return (
    <div className="h-screen-dynamic fixed inset-0 z-[55] flex w-full justify-center overflow-x-hidden overflow-y-scroll overscroll-contain p-14">
      <motion.div
        className="fixed inset-0 z-[-1] h-full w-full bg-blackTransparent backdrop-blur-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => setActiveTab(null)}
        transition={{
          duration: 0.4,
          ease: 'easeInOut',
        }}
      ></motion.div>
      <motion.div
        className="h-full w-full max-w-[700px]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{
          duration: 0.4,
          ease: 'easeInOut',
        }}
      >
        <div className="w-full py-60">
          <div className="sticky top-60 z-[9] flex w-full justify-between pb-20 text-11 text-darkGray">
            <div className="scrollbar-hidden flex max-w-[calc(100%-50px)] gap-4 overflow-x-scroll rounded-8 bg-white p-4 shadow-sm">
              {items.map((item) => {
                return (
                  <Button
                    key={item.title}
                    id={handleize(item.title)}
                    active={activeTab === handleize(item.title)}
                    onClick={() => setActiveTab(handleize(item.title))}
                    className={` whitespace-nowrap py-8 leading-100 md:py-10 md:hover:bg-[#DEDEDE] ${
                      activeTab === handleize(item.title) ? 'bg-[#DEDEDE]' : ''
                    }`}
                  >
                    {item.title}
                  </Button>
                )
              })}
            </div>
            <div className="rounded-8 bg-white p-4 text-11 shadow-sm">
              <Button
                className="h-full px-8 py-8 md:px-10 md:py-10 md:hover:!bg-[#DEDEDE]"
                onClick={() => setActiveTab(null)}
              >
                <CloseIcon />
              </Button>
            </div>
          </div>
          <div className="rounded-12 bg-white px-20 py-30">
            <AnimatePresence mode="wait">
              {items.map((item) => {
                if (handleize(item.title) !== activeTab) return null
                const url = getUrlFromLink(item.link)
                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="flex flex-col gap-4 text-12 leading-100 md:text-20">
                      <div className="text-midGray ">{item.title}</div>
                      {item.subtitle ? (
                        <div className="text-gray">{item.subtitle}</div>
                      ) : null}
                    </div>
                    <div className="max-w-[400px] pt-30 text-12 leading-120 text-gray md:text-14">
                      <PortableText value={item.body} />
                    </div>
                    <div
                      className="relative mt-30 w-full overflow-hidden rounded-12"
                      style={{ aspectRatio: item.image.aspect ?? '1/1' }}
                    >
                      <Image
                        src={urlForImage(item.image)
                          .width(800)
                          .quality(95)
                          .auto('format')
                          .url()}
                        fill
                        alt=""
                        style={{ objectFit: 'cover' }}
                        placeholder="blur"
                        blurDataURL={item.image.lqip}
                      />
                    </div>
                    {url ? (
                      <Link
                        scroll={false}
                        href={url}
                        className="mt-12 w-full border-t-1 border-borderGray pt-20 text-center text-darkGray underline opacity-50 transition-opacity duration-300 md:hover:opacity-100"
                      >
                        {item.link.label}
                      </Link>
                    ) : null}
                  </motion.div>
                )
              })}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
